import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { FAQS } from "@/data/faqs";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } } };

export default function Faq() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const breadcrumbLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://nc-mycology.com/" },
      { "@type": "ListItem", position: 2, name: "FAQ", item: "https://nc-mycology.com/faq" },
    ],
  };

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20" data-testid="faq-page">
      <Seo
        title="Mushroom Cultivation FAQ"
        description="Answers to common questions about growing mushrooms — liquid cultures, contamination, sterile technique, shipping, and beginner tips from NC Mycology."
        path="/faq"
      >
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Seo>

      <nav className="font-mono-label text-[11px] text-muted-foreground mb-8" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-primary transition-colors">Home</Link>
        <span className="mx-2 opacity-50">/</span>
        <span className="text-foreground">FAQ</span>
      </nav>

      <motion.header variants={fadeUp} initial="hidden" animate="visible" className="mb-14">
        <p className="font-mono-label text-xs text-primary mb-3">Answers</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
          Mushroom cultivation FAQ
        </h1>
        <p className="mt-6 text-base text-muted-foreground max-w-2xl leading-relaxed">
          Everything home growers ask us about liquid cultures, contamination, sterile technique,
          shipping, and getting your first flush — answered by the NC Mycology lab.
        </p>
      </motion.header>

      <div className="divide-y divide-border border-t border-border">
        {FAQS.map((f, i) => (
          <motion.article
            key={i}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            transition={{ delay: (i % 4) * 0.05 }}
            className="py-8"
            data-testid={`faq-page-item-${i}`}
          >
            <h2 className="font-display text-xl sm:text-2xl font-normal leading-snug mb-3">{f.q}</h2>
            <p className="text-[15px] text-muted-foreground leading-relaxed">{f.a}</p>
          </motion.article>
        ))}
      </div>

      <div className="mt-16 border border-border bg-card/40 rounded-sm p-8 sm:p-10 text-center">
        <h2 className="font-display text-2xl sm:text-3xl font-light mb-3">Still have a question?</h2>
        <p className="text-sm text-muted-foreground mb-7 max-w-md mx-auto">
          Our team grows with the same cultures we sell — reach out and we'll help you dial in a clean, healthy grow.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact" data-testid="faq-page-contact"
            className="inline-flex items-center gap-2 bg-primary hover:brightness-110 text-primary-foreground font-mono-label text-xs px-7 py-3.5 rounded-full transition-[filter] active:scale-95">
            Contact Us <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/shop" data-testid="faq-page-shop"
            className="inline-flex items-center gap-2 border border-border hover:border-primary hover:text-primary font-mono-label text-xs px-7 py-3.5 rounded-full transition-colors">
            Shop Cultures
          </Link>
        </div>
      </div>
    </div>
  );
}
